import { HttpClient } from '@angular/common/http';
import { Injectable, computed, effect, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TenantService } from '../tenant/tenant.service';
import { I18nStore } from './i18n.store';
import {
  DEFAULT_LOCALE,
  type I18nSource,
  type LocaleCode,
  type RuntimeLocale,
  type TranslationDictionary,
} from './i18n.types';
import {
  detectBrowserLocale,
  getByPath,
  interpolate,
  normalizeDictionaryKeysToLowercase,
  normalizeLocale,
  resolveByPriority,
} from './i18n.utils';

const LOCAL_STORAGE_KEY = 'taskeo.i18n.locale';
/** Mesma chave que `AuthService` — leitura direta evita DI circular (I18n → Auth → … → Http → I18n). */
const AUTH_TOKEN_STORAGE_KEY = 'taskeo.auth.token';

@Injectable({ providedIn: 'root' })
export class I18nService {
  private readonly http = inject(HttpClient);
  private readonly tenant = inject(TenantService);
  private readonly store = inject(I18nStore);
  private readonly loadedLocales = new Set<RuntimeLocale>();

  readonly locale = this.store.locale;
  readonly source = this.store.source;
  readonly loading = this.store.loading;
  readonly dictionaries = this.store.dictionaries;

  readonly localeOptions = computed(() => [
    { value: 'pt-BR' as RuntimeLocale, label: 'Portugues (Brasil)' },
    { value: 'en-US' as RuntimeLocale, label: 'English (US)' },
  ]);

  constructor() {
    effect(() => {
      void this.tenant.activeCompany()?.id;
      void this.tenant.activeCompany()?.language;
      void this.refreshFromPriority();
    });
  }

  async init(): Promise<void> {
    await this.refreshFromPriority();
  }

  async refreshFromPriority(): Promise<void> {
    const companyLocale = this.tenant.activeCompany()?.language ?? null;
    const localLocale = this.readStoredLocale();
    const browserLocale = detectBrowserLocale();

    const inWorkspace =
      this.hasAuthTokenInStorage() && this.tenant.getActiveCompanyId() != null;
    const preferBrowserOverLocal = !inWorkspace;

    const resolved = resolveByPriority({
      companyLocale,
      localLocale,
      browserLocale,
      preferBrowserOverLocal,
    });

    const persistLocale =
      !(preferBrowserOverLocal && resolved.source === 'browser');

    await this.setLocale(resolved.locale, resolved.source, { persist: persistLocale });
  }

  async setLocale(
    locale: RuntimeLocale,
    source: I18nSource = 'local',
    options?: { persist?: boolean }
  ): Promise<void> {
    await this.ensureLocaleLoaded(locale);
    this.store.setLocale(locale, source);
    this.updateDocumentLanguage(locale);

    if (options?.persist !== false) {
      this.storeLocale(locale);
    }
  }

  t(key: string, params?: Record<string, string | number>, fallback?: string): string {
    const locale = this.locale();
    const dictionaries = this.dictionaries();
    const current = getByPath(dictionaries[locale], key);
    const base = getByPath(dictionaries[DEFAULT_LOCALE], key);
    const resolved = current ?? base ?? fallback ?? key;
    return interpolate(resolved, params);
  }

  private async ensureLocaleLoaded(locale: RuntimeLocale): Promise<void> {
    if (this.loadedLocales.has(locale)) return;
    this.store.setLoading(true);

    try {
      const dictionary = await this.loadDictionary(locale);
      this.store.setDictionary(locale, dictionary);
      this.loadedLocales.add(locale);
    } finally {
      this.store.setLoading(false);
    }
  }

  private async loadDictionary(locale: RuntimeLocale): Promise<TranslationDictionary> {
    // Query evita JSON antigo preso em cache do navegador após deploy (traduções “não mudam”).
    const path = `assets/i18n/${locale}.json?v=20260324c`;
    try {
      const dictionary = await firstValueFrom(this.http.get<TranslationDictionary>(path));
      return normalizeDictionaryKeysToLowercase(dictionary);
    } catch {
      return {};
    }
  }

  private hasAuthTokenInStorage(): boolean {
    try {
      return !!localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)?.trim();
    } catch {
      return false;
    }
  }

  private readStoredLocale(): LocaleCode | null {
    try {
      return normalizeLocale(localStorage.getItem(LOCAL_STORAGE_KEY));
    } catch {
      return null;
    }
  }

  private storeLocale(locale: RuntimeLocale): void {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, locale);
    } catch {}
  }

  private updateDocumentLanguage(locale: RuntimeLocale): void {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = locale;
  }
}
